import React from 'react';
import { INewFeedMessageActivity, Member } from './interfaces';
import { Paper, Typography } from '@mui/material';
import { ActivityPost } from '@components/feed/Feed/components/ActivityPost';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from '@routes/routes';
import { getPortalContentPageRoute } from '@helpers/contentPageHelpers';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAuth } from '@context/AuthProvider';

export const NewFeedMessageActivity: React.FC<INewFeedMessageActivity> = ({
  date,
  title,
  slug,
  members,
  membersCountOther = 0,
  feedType,
  message,
  icon,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const memberList = members ?? [];

  const getMemberName = (member: Member | undefined): string | undefined => {
    if (!member) {
      return undefined;
    }

    if (member.id === currentUser?.member.id) {
      return t('words.you');
    }

    return member.firstName;
  };

  const first = getMemberName(memberList[0]);
  const second = getMemberName(memberList[1]);
  const third = getMemberName(memberList[2]);
  const others = membersCountOther;

  const key = (() => {
    if (membersCountOther > 0) {
      return 'manyAuthors';
    }

    if (memberList.length === 2) {
      return 'twoAuthors';
    }

    if (memberList.length === 3) {
      return 'threeAuthors';
    }

    return 'singleAuthor';
  })();

  const activityTitle = (
    <Trans
      i18nKey={`activities:newFeedMessage.${key}`}
      values={{
        first,
        second,
        third,
        others,
        type: t(`activities:newFeedMessage.resources.${feedType}`),
        title,
      }}
      components={{
        bold: <strong />,
      }}
    />
  );

  const route = (() => {
    if (feedType === 'competition') {
      return ROUTES.portal.competitions.details(slug);
    }

    if (
      feedType === 'news-item' ||
      feedType === 'association-instruction' ||
      feedType === 'association-rules-and-regulations'
    ) {
      return getPortalContentPageRoute(feedType, slug);
    }

    return '';
  })();

  const feedTypeIcon = (() => {
    if (feedType === 'association-rules-and-regulations') {
      return HealthAndSafetyIcon;
    }

    if (feedType === 'association-instruction') {
      return MenuBookIcon;
    }

    if (feedType === 'news-item') {
      return NewspaperIcon;
    }

    if (feedType === 'competition') {
      return EmojiEventsIcon;
    }

    return QuestionMarkIcon;
  })();

  const showMessage = memberList.length === 1 && message;

  return (
    <ActivityPost
      date={date}
      icon={feedTypeIcon}
      badgeIcon={QuestionAnswerIcon}
      title={activityTitle}
      onClick={() => {
        if (route) {
          navigate(route);
        }
      }}
    >
      {showMessage && (
        <Paper
          elevation={2}
          sx={{
            p: '10px',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-line',
            }}
          >
            {message}
          </Typography>
        </Paper>
      )}
    </ActivityPost>
  );
};
